import React from 'react';
import { useAppSelector } from 'src/redux/hooks';
import { AppState } from 'src/redux/store';
import { selectModuleDataByIds, determineSectionProgressIcon } from 'src/redux/slices/learningModules';
import { ContentProgressStatus } from 'src/types/contentProgress';
import { LearningModuleStateData } from 'src/types/state/learningModule';
import { LearningPathwaySection } from 'src/types/state/learningPathway';
import {
  ModuleSectionAccordionItem,
  WorkflowTrackerStepState,
} from '@amzn/seller-university-ui-components';
import { t, resolveContent } from 'src/utils/translation';
import { BUNDLE_NAME, LEARNING_PATHWAY_STRING_ID } from 'src/constants/localization';
import { useBundle } from '@amzn/react-arb-tools';
import { timeConverter } from 'src/utils/timeFormat';
import { REF_TAG } from 'src/constants/refTag';
import useMetrics from 'src/metrics/useUserMetrics';
import { FeatureName } from 'src/metrics/metricsFields';

interface ModuleSectionItemProps {
  section: LearningPathwaySection;
  index: number;
  onModuleSelect: (moduleId: string) => void;
  expanded?: boolean;
  isLocalized?: boolean;
}

const ModuleSectionItem: React.FC<ModuleSectionItemProps> = ({
  section,
  index,
  onModuleSelect,
  expanded = false,
  isLocalized = false,
}) => {
  // Get module data for all modules in this section, memoized to prevent unnecessary recalculations
  const moduleDataMap = useAppSelector(
    (state: AppState) => selectModuleDataByIds(state, section.learningModuleIds),
  );

  // Get the section's progress icon based on the status of its modules, efficiently computed and cached
  const sectionProgressIcon = useAppSelector(
    (state: AppState) => determineSectionProgressIcon(state, section.learningModuleIds),
  );

  /**
   * Retrieves the localization bundle for learning pathway content
   * Used for translating UI elements and formatting text
   */
  const [learningPathwayBundle] = useBundle(
    BUNDLE_NAME.LEARNING_PATHWAY,
  );

  /**
   * Maps content progress status to UI workflow tracker states
   * @param status - The content progress status from the API
   * @returns The corresponding workflow tracker state for the UI component
   */
  const mapProgressStatus = (status: ContentProgressStatus): WorkflowTrackerStepState => {
    switch (status) {
      case ContentProgressStatus.COMPLETED:
        return 'complete';
      case ContentProgressStatus.IN_PROGRESS:
        return 'in-progress';
      case ContentProgressStatus.NOT_STARTED:
      default:
        return 'todo';
    }
  };

  /**
   * Maps module instance type to UI content variant
   * @param instanceType - The type of module instance (e.g., 'VIDEO', 'DOCUMENT')
   * @returns The corresponding content variant for the UI component
   */
  const mapInstanceTypeToContentVariant = (instanceType?: string) => {
    switch (instanceType?.toUpperCase()) {
      case 'VIDEO':
        return 'Video' as const;
      case 'DOCUMENT':
      case undefined:
      default:
        return 'Pdf' as const;
    }
  };

  /**
   * Retrieves the default instance from a learning module based on its default language.
   * Returns undefined if instanceMap is empty (e.g., under PolyD migration T1 when
   * no instance data exists for the requested locale).
   * @param module - The learning module data
   * @returns The default instance for the module, or undefined if not available
   */
  const getDefaultInstance = (module: LearningModuleStateData) => module.instanceMap?.[module.defaultLanguage];

  /**
   * Generates formatted metadata text based on the module's type and properties
   * For videos: displays duration in MM:SS format
   * For documents: displays page count
   * Returns empty array if no default instance is available (empty instanceMap).
   * @param module - The learning module data
   * @returns Formatted metadata array for display in the UI
   */
  const getModuleMetadata = (module: LearningModuleStateData): string[] => {
    const defaultInstance = getDefaultInstance(module);

    if (!defaultInstance) {
      return [];
    }

    if (defaultInstance.instanceType?.toUpperCase() === 'VIDEO' && defaultInstance.duration) {
      // For video, convert duration from seconds to minutes:seconds format
      return [timeConverter(defaultInstance.duration)];
    }
    if (defaultInstance.instanceType?.toUpperCase() === 'DOCUMENT' && defaultInstance.pageSize) {
      // For PDF, show page count
      return [t(
        learningPathwayBundle,
        LEARNING_PATHWAY_STRING_ID.LEARNING_PATHWAY_PDF_PAGES,
        {
          count: defaultInstance.pageSize,
        },
      )];
    }
    return [];
  };

  // Initialize metrics hook for tracking user interactions
  const { clickMetric } = useMetrics();

  // Handler for Learning Module Section box clicks
  const handleModuleSectionBoxClick = (moduleId: string) => {
    clickMetric({
      feature: FeatureName.LearningPathwaySection,
      metadata: {
        sectionId: section.sectionId,
        sectionTitle: section.title,
        moduleId,
      },
    });
    onModuleSelect(moduleId);
  };

  // Handler for Module Section CTA button clicks
  /* istanbul ignore next */
  const handleModuleSectionCtaClick = () => {
    clickMetric({
      feature: FeatureName.LearningPathwaySectionCta,
      metadata: {
        sectionId: section.sectionId,
        sectionTitle: section.title,
        ctaText: section.ctaText,
        ctaLink: section.ctaLink,
      },
    });
  };

  return (
    <ModuleSectionAccordionItem
      moduleSectionLabel={t(
        learningPathwayBundle,
        LEARNING_PATHWAY_STRING_ID.LEARNING_PATHWAY_SECTION_LABEL,
        { number: index + 1 },
      )}
      moduleSectionTitle={resolveContent(
        learningPathwayBundle,
        section.title,
        isLocalized,
      )}
      moduleSectionDescription={resolveContent(
        learningPathwayBundle,
        section.description,
        isLocalized,
      )}
      progressIcon={sectionProgressIcon}
      totalModulesLabel={t(
        learningPathwayBundle,
        LEARNING_PATHWAY_STRING_ID.LEARNING_PATHWAY_TOTAL_MODULES_LABEL,
        {
          count: section.learningModuleIds.length,
        },
      )}
      ModuleSectionBodyItems={
        section.learningModuleIds
          .map((moduleId) => moduleDataMap[moduleId])
          .map((module) => ({
            progress: {
              state: mapProgressStatus(module.moduleProgressStatus),
              id: module.moduleId,
            },
            content: {
              id: module.moduleId,
              title: module.title,
              metadata: getModuleMetadata(module).join(', '),
              moduleVariant: getDefaultInstance(module)
                ? mapInstanceTypeToContentVariant(getDefaultInstance(module)!.instanceType)
                : 'Pdf' as const,
              previewDescription: module.description,
              imageSrc: module.thumbnailLink,
              onClick: () => handleModuleSectionBoxClick(module.moduleId),
            },
          }
          ))
      }
      expanded={expanded}
      ctaButton={section.ctaLink && section.ctaText
        ? {
          label: resolveContent(
            learningPathwayBundle,
            section.ctaText,
            isLocalized,
          ),
          href: section.ctaLink,
          refTag: REF_TAG.LEARNING_PATHWAY_SECTION_CTA_BTN,
          onClick: handleModuleSectionCtaClick,
        }
        : undefined}
    />
  );
};

export default ModuleSectionItem;
